import "./style.scss";

import Paper from "@mui/material/Paper";
import type { CSSProperties } from "@mui/material/styles/createMixins";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { BeneficiaryLine } from "components/URSSAFModal";
import { isValidDDMMYYYYDate, numberToLetter } from "helpers/utils";
import { useAdapterLocale } from "hooks/useAdapterLocale";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { BasicPayloadError } from "../LastStep";

interface CorrectionTableProps {
    lines: BeneficiaryLine[];
    errors: BasicPayloadError[];
    onChange?: (data: BeneficiaryLine[]) => void;
    onSubmit: (data: BeneficiaryLine[]) => void;
}

/**
 * Map server error to their corresponding human-readable error label
 */
export const errorI18nTable = {
    UnauthorizedProfileType:
        "Le type de cotisant inscrit ne correspond pas au type de diffuseur que vous avez renseigné",
    MissingMandatoryField: "Ce champ est obligatoire et n'a pas été renseigné",
    WrongDateFormat: "Le format de la date doit être JJ/MM/AAAA",
    WrongLength:
        "La longueur du NIR est incorrecte (clé de sécurité manquante ?), s'il s'agit d'un NIR étranger merci de ne pas le renseigner",
    WrongControlKey: "La clé de sécurité renseignée ne correspond pas au NIR renseigné",
    RequiredField: "Ce champs est obligatoire",
    WrongValue: "La valeur renseignée est incorrecte",
    WrongZipCodeFormat: "Le code postal doit être de 5 chiffres et ne contenir aucune lettre ou symbole",
};

/**
 * Map field/column keys to their corresponding human-readable label
 */
export const fieldKeyToI18nMapping = {
    profileType: "Type de cotisant",
    socialSecurityNumber: "Numéro de sécurité sociale",
    lastname: "Nom",
    firstname: "Prénom",
    streetNumber: "Numéro de voie",
    streetNumberMultiplierAdverb: "Indice du numéro de voie",
    street: "Nom de voie",
    postalCode: "Code postal",
    city: "Ville",
    country: "Pays",
    birthDate: "Date de naissance",
    profitType: "Nature du revenu artistique",
    profitNature: "Nature de l'oeuvre",
    paymentDate: "Date de paiement",
    siretNumber: "Numéro de SIRET ou RNA",
    grossDuty: "Rémunération brute ou montant versé",
    dcaRevenue: "DCA — Chiffres d'affaires TTC",
    dcaCommission: "DCA/OVV — Commissions TTC",
};

export default function CorrectionTable(props: CorrectionTableProps) {
    const [t] = useTranslation();
    const adapterLocale = useAdapterLocale();

    /**
     * Lines to be shown (from fileLine to index),
     * we avoid duplicates
     */
    const errorsFileLine = Array.from(new Set(props.errors?.map((error) => error.fileLine - 2)));

    /**
     * We map errors to their corresponding field
     * also attach fileLine and original idx
     */
    const linesWithErrors = props.lines
        .map((line, idx) => ({
            idx,
            ...line,
            fileLine: idx + 2,
            errors: props.errors?.filter((error) => error.fileLine - 2 == idx),
        }))
        .filter((_, idx) => errorsFileLine.includes(idx));

    /**
     * Column headers (and missing ones from error.field property)
     */
    const headers = Array.from(new Set(Object.keys(props.lines[0])?.concat(props.errors?.map((error) => error.field))));

    const [data, setData] = useState<BeneficiaryLine[]>(props.lines);

    const updateRow = (atPosition: number, field: keyof BeneficiaryLine, value: any) => {
        const _data = [...data];
        (_data[atPosition] as any)[field] = value;
        setData(_data);
    };

    const tableCellColumnNameStyle: CSSProperties = {
        backgroundColor: "#f6f6f6",
        fontSize: "14px",
        borderRight: "1px solid #eaeaea",
        borderBottomColor: "#eaeaea",
        fontWeight: "600",
        zIndex: 3,
    };

    return (
        <div className="correction-table">
            <p>
                {errorsFileLine.length} {t("Declare.CorrectionTableLines")}
            </p>
            <ScrollShadowWrapper style={{ maxHeight: "60vh" }}>
                <TableContainer component={Paper} style={{ boxShadow: "none", border: "1px solid #eaeaea" }}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={tableCellColumnNameStyle}>Ligne</TableCell>
                                {headers.map((_, idx) => (
                                    <TableCell
                                        sx={tableCellColumnNameStyle}
                                        key={`hl${idx}`}
                                        align="center"
                                        variant="head"
                                    >
                                        {numberToLetter(idx + 1)}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow>
                                <TableCell sx={tableCellColumnNameStyle}>1</TableCell>
                                {headers.map((header, idx) => (
                                    <TableCell key={`h${idx}`} variant="head">
                                        {(fieldKeyToI18nMapping as any)[header]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {linesWithErrors.map((row) => (
                                <TableRow
                                    key={`line${row.idx}`}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                    <TableCell sx={tableCellColumnNameStyle} component="th" scope="col" variant="head">
                                        {row.fileLine}
                                    </TableCell>
                                    {headers.map((col, jdx) => {
                                        if (["errors", "idx", "fileLine"].includes(col)) return null;

                                        // if (["grossDuty", "dcaRevenue", "dcaCommission"].includes(col)) {
                                        // 	(row as any)[col as any] = +(row as any)[col as any] / 100;
                                        // }

                                        if (row.errors.some((error: BasicPayloadError) => error.field === col)) {
                                            if (col === "paymentDate" || col === "birthDate") {
                                                const dateValue = data[row.idx][col];
                                                const errorMessage = (errorI18nTable as any)[
                                                    row.errors.find((error: any) => error.field == col)?.message ?? ""
                                                ];

                                                const isValidDate = isValidDDMMYYYYDate(dateValue as string);
                                                const value =
                                                    isValidDate && dateValue
                                                        ? new Date((dateValue as string).split("/").reverse().join("-"))
                                                        : null;

                                                return (
                                                    <TableCell
                                                        component="td"
                                                        scope="row"
                                                        key={`col${jdx}`}
                                                        sx={{ minWidth: "200px" }}
                                                    >
                                                        <LocalizationProvider
                                                            dateAdapter={AdapterDateFns}
                                                            adapterLocale={adapterLocale}
                                                        >
                                                            <DatePicker
                                                                label={(fieldKeyToI18nMapping as any)[col]}
                                                                views={["day", "month", "year"]}
                                                                // format="dd/MM/yyyy"
                                                                value={value}
                                                                renderInput={(params) => (
                                                                    <StyledTextField
                                                                        {...params}
                                                                        helperText={
                                                                            !isValidDate
                                                                                ? `${dateValue} \r\n` +
                                                                                  (errorMessage ?? "Champs invalide")
                                                                                : null
                                                                        }
                                                                    />
                                                                )}
                                                                onChange={(date: Date | null) =>
                                                                    updateRow(
                                                                        row.idx,
                                                                        col,
                                                                        (date as Date)?.toLocaleDateString("fr-FR") ??
                                                                            "",
                                                                    )
                                                                }
                                                            />
                                                        </LocalizationProvider>
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell
                                                    component="td"
                                                    scope="row"
                                                    key={`col${jdx}`}
                                                    sx={{ minWidth: "200px" }}
                                                >
                                                    <TextField
                                                        id={`f${jdx}-${col}`}
                                                        label={(fieldKeyToI18nMapping as any)[col]}
                                                        value={(data[row.idx] as any)[col]}
                                                        helperText={
                                                            t(
                                                                `URSSAFErrors.${[
                                                                    row.errors.find((error: any) => error.field == col)
                                                                        ?.message ?? "undefined",
                                                                ]}`,
                                                            ) ?? "Champs invalide"
                                                        }
                                                        onChange={(e) =>
                                                            updateRow(
                                                                row.idx,
                                                                col as keyof BeneficiaryLine,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                            );
                                        }

                                        return (
                                            <TableCell component="td" scope="row" key={`col${jdx}`}>
                                                {(row as any)[col as any]}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ScrollShadowWrapper>

            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                <StyledButton buttonType="secondary" onClick={() => props.onSubmit(data)}>
                    {t("Declare.CorrectionTableRestart")}
                </StyledButton>
                <StyledButton buttonType="primary" onClick={() => props.onSubmit(data)}>
                    {t("Declare.CorrectionTableRetry")}
                </StyledButton>
            </div>
        </div>
    );
}
